import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';

import { Cards } from 'src/components/cards/Cards';
import { CategoryEeccIcon } from 'src/scenes/Project/scenes/Dashboard/components/CategoryEeccIcon';
import { Triangle, TriangleDown } from 'src/utils/mdIcons';

const useStyles = makeStyles(theme => ({
  tableContainer: {
    maxHeight: 600,
  },
  tableCell: {
    fontSize: '0.875rem',
    padding: '12px 16px',
  },
  headerCell: {
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: '12px 16px',
  },
  textCell: {
    fontSize: '0.875rem',
    lineHeight: 1.2,
    overflow: 'hidden',
    wordWrap: 'break-word',
    hyphens: 'auto',
  },
  conservationChip: {
    fontSize: '0.75rem',
    height: 24,
  },
  populationCell: {
    textAlign: 'right',
  },
  conservationAvatar: {
    width: '32px',
    height: '32px',
    fontSize: '0.875rem',
    fontWeight: 'bold',
  },
  trendIcon: {
    fontSize: '12px',
  },
  variationUp: {
    color: theme.palette.success.main,
  },
  variationDown: {
    color: theme.palette.error.main,
  },
  scientificName: {
    fontStyle: 'italic',
    textTransform: 'capitalize',
  },
}));

const SpeciesInventoryTable = ({ comparisonYears = [ 2020, 2024 ], speciesData = [], kingdomFilter = 'total' }) => {

  const classes = useStyles();
  const [ year1, year2 ] = comparisonYears;

  // Filtrar especies según el filtro de reino y eliminar las que tienen 0 en ambos años
  const filteredSpeciesData = useMemo(() =>
    speciesData.filter(species => {
      const population1 = species.occurrences?.[year1] || 0;
      const population2 = species.occurrences?.[year2] || 0;

      // Eliminar especies con 0 en ambos años
      if (population1 === 0 && population2 === 0) {
        return false;
      }

      // Filtrar por reino
      if (kingdomFilter === 'flora') {
        return species.kingdom === 'Plantae';
      } else if (kingdomFilter === 'fauna') {
        return species.kingdom === 'Animalia';
      }

      // Para 'total', mostrar todas las especies
      return true;
    })
  , [ speciesData, kingdomFilter, year1, year2 ]);

  // Definir columnas para Cards
  const columns = [
    {
      title: 'Especie',
      field: 'scientificName',
      headerTextAlign: 'center',
      render: item => (
        <Box textAlign="center">
          <Box fontWeight={600} className={classes.scientificName}>
            {item.scientificName}
          </Box>
        </Box>
      ),
    },
    {
      title: 'Estado de conservación',
      field: 'categories',
      headerTextAlign: 'center',
      render: item => (
        <Box justifySelf="center">
          <CategoryEeccIcon category={item.categories} />
        </Box>
      ),
    },
    {
      title: 'Origen',
      field: 'origin',
      headerTextAlign: 'center',
      render: item => (
        <Box textAlign="center">
          {item.origin ? item.origin.charAt(0).toUpperCase() + item.origin.slice(1) : '-'}
        </Box>
      ),
    },
    {
      title: `Población ${year1}`,
      field: `population${year1}`,
      headerTextAlign: 'center',
      render: item => (
        <Box className={classes.textCell} textAlign="center">
          {(item.occurrences?.[year1] || 0).toLocaleString()}
        </Box>
      ),
    },
    {
      title: `Población ${year2}`,
      field: `population${year2}`,
      headerTextAlign: 'center',
      render: item => (
        <Box className={classes.textCell} textAlign="center">
          {(item.occurrences?.[year2] || 0).toLocaleString()}
        </Box>
      ),
    },
    {
      title: 'Cambio',
      field: 'change',
      headerTextAlign: 'center',
      render: item => {
        const population1 = item.occurrences?.[year1] || 0;
        const population2 = item.occurrences?.[year2] || 0;
        const change = population2 - population1;
        let changePercent = population1 > 0 ? ((change / population1) * 100).toFixed(0) : 0;
        changePercent = String(changePercent).replace('.', ',');
        return (
          <Box textAlign="center">
            {change > 0 ? <Triangle className={clsx(classes.trendIcon, classes.variationUp)} />
            : change < 0 ? <TriangleDown className={clsx(classes.trendIcon, classes.variationDown)} />
            : null
            }
            <Typography
              variant="body2"
              className={clsx(classes.textCell, {
                [classes.variationUp]: change > 0,
                [classes.variationDown]: change < 0,
              })}
              component="span"
            >
              &nbsp;{change >= 0 ? '+' : '-'}{Math.abs(change)} ({changePercent}%)
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Cards
      values={filteredSpeciesData}
      columns={columns}
      variant="condensed"
      showPagination={true}
    />
  );
};

SpeciesInventoryTable.propTypes = {
  comparisonYears: PropTypes.arrayOf(PropTypes.number),
  kingdomFilter: PropTypes.oneOf([ 'total', 'flora', 'fauna' ]),
  speciesData: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    scientificName: PropTypes.string,
    kingdom: PropTypes.string,
    origin: PropTypes.string,
    categories: PropTypes.string,
    occurrences: PropTypes.object,
  })),
};

export {
  SpeciesInventoryTable,
};